import seoData from "./routeSeo.json";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import type { RouteSettingsOverride } from "../../MathFactsGenerator/RouteSettings.ts";
import { DEFAULT_LOCALE, Locale } from "../i18n/Locale.ts";
import type { ManipulativeSpec } from "../../Manipulatives/kinds.ts";
import type { CardGameId } from "../../CardGames/games.ts";

export interface FaqEntry {
    q: string;
    a: string;
}

/** One tile on a grade hub: a page, and what that skill is at this grade. */
export interface SkillTile {
    slug: string;
    blurb: string;
}

/** A strand of a grade hub - "Fractions", "Time and money" - and its tiles. */
export interface SkillSection {
    heading: string;
    skills: SkillTile[];
}

/**
 * One activity in a substitute pack (ADR 0083): an existing page, the blurb that says what it
 * holds, and the settings it opens with beyond that page's own. The tile's title is the page's h1.
 */
export interface PackActivity {
    slug: string;
    blurb: string;
    settings?: RouteSettingsOverride;
    /** What a placement check calls this strand: the heading over its items and the row on the key. */
    name?: string;
}

/**
 * How a placement check (ADR 0086) is put together out of a pack's `activities`, which are its strands:
 * each inner list of `sheets` is the indexes of the strands on one spiral-style sheet, `stories` is the
 * index of the activity whose page the four stories come from, and the fact finder says which activity
 * is its page and which operations' ladder it places a child on.
 */
export interface CheckSpec {
    sheets: number[][];
    stories: number;
    factFinder: { activity: number; operations: string[] };
}

/**
 * A pack: the grade it is for, the grade it reviews, and its activities in order. A substitute pack
 * (the default, ADR 0083) has four, and each is a sheet. A placement check (`kind`, ADR 0086) has a
 * strand for each skill it samples, and `check` says how they are laid out.
 */
export interface PackSpec {
    kind?: "placement";
    grade: number;
    reviews: string;
    activities: PackActivity[];
    check?: CheckSpec;
}

export interface RouteSeo {
    slug: string;
    legacyPaths?: string[];
    operation?: Operations;
    /** Names the chart component a chart route renders. */
    chart?: string;
    /** The operation an on-screen flash-card route drills. */
    flashCard?: Operations;
    /** The operation a timed/untimed quiz route drills. */
    quiz?: Operations;
    /**
     * Makes a quiz route a game instead (ADRs 0053-0057, 0059, 0060, 0103, 0124, 0128). Only meaningful with
     * `quiz` - on its own it would be invisible to every route filter here,
     * which routeSeo.test.ts asserts against.
     */
    game?: "drop" | "chart" | "product" | "munch" | "collie" | "soccer" | "plates" | "football" | "runner" | "merge" | "shop" | "picture" | "crossword";
    /**
     * Makes the route the daily puzzle page (ADR 0127). Not a `quiz`: that would claim an operation the
     * page does not have. The value names the puzzle; only "groups" (Equal Groups) so far.
     */
    dailyPuzzle?: "groups";
    /**
     * Makes the route a printable card game page (ADR 0135). The value is the game's id in
     * `CardGames/games.ts`; the page and the PDF both read the rules from there.
     */
    cardGame?: CardGameId;
    /** Prose pages - About, Privacy - which render through ContentPage. */
    contentPage?: boolean;
    /**
     * Renders the analytics consent control on this prose page (ADR 0025
     * amendment 2 §G). Carried as manifest data rather than a `/privacy`
     * slug check in ContentPage so a localised policy page picks it up by
     * being declared, like every other per-route behaviour here.
     */
    consentControl?: boolean;
    /** The one all-pages index, /all-worksheets. See ADR 0027 - now one per locale, ADR 0051 §8. */
    indexPage?: boolean;
    noindex?: boolean;
    /** Which footer column this route belongs in; see footerGroupOf. */
    group?: string;
    /**
     * The interface locale this route is written for (ADR 0051 §8). Absent
     * means English - every route before this part left it unset, and stays
     * that way. Part 2's `uiLang` query key remains a review override; a
     * route with a `locale` is the real source LocaleContext resolves from.
     */
    locale?: Locale;
    /**
     * Groups this route with its language alternates for `hreflang`. Every
     * route sharing a `translationGroup` is an alternate of every other one
     * in the group - the group is the single pairing both hreflang
     * directions are derived from (see `translationsOf`), rather than two
     * per-route lists that could name each other inconsistently.
     */
    translationGroup?: string;
    /** Generator settings this route opens with, beyond its operation. */
    settings?: RouteSettingsOverride;
    title: string;
    description: string;
    h1?: string;
    intro?: string[];
    /**
     * What a student does on this sheet, two or three short phrases each
     * starting with a verb (ADR 0065). Every English worksheet page has them;
     * localised routes have none.
     */
    objectives?: string[];
    /**
     * Common Core math codes this sheet practises - at most three, each in
     * standards.json, and only where the page's own copy backs the claim
     * (ADR 0065 §3). Absent where no standard honestly matches.
     */
    standards?: string[];
    faq?: FaqEntry[];
    /**
     * Makes the route a grade hub (ADR 0062): tiles grouped by strand, each
     * linking to an existing page. A tile's title is the target route's h1;
     * only the blurb lives here, because a skill reads differently at each
     * grade. Rendered by Landing/GradePage.tsx and by prerender.js.
     */
    skillSections?: SkillSection[];
    /**
     * Makes the route a substitute pack page (ADR 0083): one preview of a cover, four activities
     * and their keys. Rendered by Landing/PackPage.tsx and by prerender.js.
     */
    pack?: PackSpec;
    /**
     * An interactive manipulative shown on this worksheet page, between the copy and the FAQ
     * (ADR 0117). Only on routes with `operation`; English pages only for now.
     */
    manipulative?: ManipulativeSpec;
}

export const SITE_NAME: string = seoData.siteName;
export const SITE_ORIGIN: string = seoData.origin;
export const DEFAULT_IMAGE: string = seoData.defaultImage;
export const DEFAULT_IMAGE_ALT: string = seoData.defaultImageAlt;
export const DEFAULT_IMAGE_WIDTH: number = seoData.defaultImageWidth;
export const DEFAULT_IMAGE_HEIGHT: number = seoData.defaultImageHeight;

export const Routes: RouteSeo[] = seoData.routes as RouteSeo[];

export function findBySlug(slug: string): RouteSeo | undefined {
    return Routes.find((route) => route.slug === slug);
}

export function findByLegacyPath(path: string): RouteSeo | undefined {
    const normalized = path.replace(/\/+$/, "").toLowerCase();
    return Routes.find((route) =>
        route.legacyPaths?.some(
            (legacy) => legacy.replace(/\/+$/, "").toLowerCase() === normalized
        )
    );
}

/** Routes that have enough data to render a full landing page. */
export function landingRoutes(): RouteSeo[] {
    return Routes.filter((route) => route.operation !== undefined && route.h1 !== undefined);
}

export interface FooterGroup {
    key: string;
    heading: string;
    /** primeicons or fontawesome class. Unused since the site nav stopped being a PrimeReact menu (ADR 0061 §6); kept as data. */
    icon: string;
    /**
     * Whether the group gets a menu in the header's top row, listing every
     * page in it (ADR 0061 §6 and its amendment).
     */
    nav?: boolean;
    /**
     * The topic's general page, which lists every other page in the group
     * under `heading` (ADR 0072). Only groups with a page whose own copy covers
     * the whole topic have one.
     */
    hub?: { slug: string; heading: string };
}

/**
 * The site's page groupings, in order, read from the manifest so the footer,
 * the static footer scripts/prerender.js writes, and the site nav all describe
 * the same site.
 */
export const FOOTER_GROUPS: FooterGroup[] = seoData.footerGroups as FooterGroup[];

/**
 * A route's interface locale (ADR 0051 §8). Absent means English, which is
 * every route added before this part and most routes added after it -
 * `locale` is only ever set on a route hand-written for that language.
 */
export function localeOf(route: RouteSeo): Locale {
    return route.locale ?? DEFAULT_LOCALE;
}

/**
 * This route's language alternates - every other route sharing its
 * `translationGroup`, in manifest order. Both hreflang directions come from
 * this one function: an English page's alternates includes the Spanish page,
 * and the Spanish page's alternates includes the English page right back,
 * because both are reading the same group rather than two separately
 * maintained lists that could name each other inconsistently. *
 * A `noindex` alternate is left out. Advertising an alternate that itself
 * says "do not index me" is a contradictory signal, and while the localised
 * routes are hidden it would have an English page still pointing crawlers at
 * pages the site is deliberately not offering. Drop the flag and they come
 * back, in both directions, with no other change.
 */
export function translationsOf(route: RouteSeo): RouteSeo[] {
    if (!route.translationGroup) {
        return [];
    }
    return Routes.filter(
        (candidate) =>
            candidate.translationGroup === route.translationGroup &&
            candidate.slug !== route.slug &&
            !candidate.noindex
    );
}

/**
 * Which footer column a route belongs in. Routes without an explicit `group`
 * fall back to what they are: a chart, or a plain worksheet.
 */
export function footerGroupOf(route: RouteSeo): string {
    if (route.group) {
        return route.group;
    }
    if (route.chart) {
        return "charts";
    }
    return "worksheets";
}

/**
 * Whether a route is a tool the footer groups - a generator, chart, deck or
 * quiz - as opposed to prose, the index, or the 404.
 *
 * `h1` alone is not the test: About and Privacy have one, and without this they
 * fall through footerGroupOf's default and land in the Worksheets column.
 * scripts/prerender.js has its own copy of this as `isLinkable`; change both.
 */
export function isToolRoute(route: RouteSeo): boolean {
    return Boolean(
        (route.operation ||
            route.chart ||
            route.flashCard ||
            route.quiz ||
            route.skillSections ||
            route.pack ||
            route.dailyPuzzle ||
            route.cardGame) &&
            route.h1
    );
}

/**
 * Linkable routes in a given footer column, in manifest order.
 *
 * `locale` defaults to English, so every existing call site (which passes
 * none) is unaffected: an English page's "fractions" column, say, still
 * means exactly the English fraction routes even though a Spanish or French
 * route could theoretically share that group key one day. A caller building
 * a localised page's own footer or index passes that route's own locale.
 */
export function routesInGroup(key: string, locale: Locale = DEFAULT_LOCALE): RouteSeo[] {
    return linkableRoutes().filter(
        (route) => footerGroupOf(route) === key && localeOf(route) === locale
    );
}

/**
 * The tiles a topic's hub page shows (ADR 0072): the group's hub heading and
 * every other page in the group, in manifest order. Null for any page that is
 * not its group's hub. scripts/prerender.js has its own copy (`topicHubOf`);
 * change both.
 */
export function topicHubOf(route: RouteSeo): { heading: string; pages: RouteSeo[] } | null {
    if (route.locale) {
        return null;
    }
    const group = FOOTER_GROUPS.find((candidate) => candidate.key === footerGroupOf(route));
    if (!group?.hub || group.hub.slug !== route.slug) {
        return null;
    }
    return {
        heading: group.hub.heading,
        pages: routesInGroup(group.key).filter((page) => page.slug !== route.slug),
    };
}

/**
 * What the home page links below the generator (ADR 0112): every grade hub, in
 * the order the manifest lists them, and every footer group's hub, in
 * FOOTER_GROUPS order, named by the group's own heading ("Fractions", not the
 * hub page's "More fraction worksheets"). Derived from the manifest so a new
 * grade page or hub appears without an edit. `noindex` routes and
 * non-English routes are left out. scripts/prerender.js has its own copy
 * (`homeLinkSections`), and LandingPage.tsx renders both; change all three
 * together.
 */
export function homeLinkSections(): { grades: RouteSeo[]; topics: { heading: string; slug: string }[] } {
    const grades = hubRoutes().filter(
        (route) => footerGroupOf(route) === 'grade' && !route.noindex && !route.locale
    );
    const topics: { heading: string; slug: string }[] = [];
    FOOTER_GROUPS.forEach((group) => {
        if (!group.hub) {
            return;
        }
        const hubRoute = Routes.find((route) => route.slug === group.hub?.slug);
        if (!hubRoute || hubRoute.noindex || hubRoute.locale) {
            return;
        }
        topics.push({ heading: group.heading, slug: group.hub.slug });
    });
    return { grades, topics };
}

/** Routes backed by a chart component rather than the worksheet generator. */
export function chartRoutes(): RouteSeo[] {
    return Routes.filter((route) => route.chart !== undefined);
}

/** Routes backed by the on-screen flash-card feature. */
export function flashCardRoutes(): RouteSeo[] {
    return Routes.filter((route) => route.flashCard !== undefined);
}

/** Grade hubs - a page of skill tiles rather than a tool (ADR 0062). */
export function hubRoutes(): RouteSeo[] {
    return Routes.filter((route) => route.skillSections !== undefined && route.h1 !== undefined);
}

/** Substitute packs (ADR 0083): a page that builds one PDF of several sheets. */
export function packRoutes(): RouteSeo[] {
    return Routes.filter((route) => route.pack !== undefined && route.h1 !== undefined);
}

/** Routes backed by the typed-answer quiz. */
export function quizRoutes(): RouteSeo[] {
    return Routes.filter((route) => route.quiz !== undefined);
}

/** The daily puzzle page (ADR 0127): its own discriminator, so it is not a quiz. */
export function dailyPuzzleRoutes(): RouteSeo[] {
    return Routes.filter((route) => route.dailyPuzzle !== undefined && route.h1 !== undefined);
}

/** The printable card game pages (ADR 0135): their own discriminator, in their own footer group. */
export function cardGameRoutes(): RouteSeo[] {
    return Routes.filter((route) => route.cardGame !== undefined && route.h1 !== undefined);
}

/**
 * Pages that are prose rather than a tool - About, Privacy.
 *
 * Kept out of linkableRoutes() on purpose: that list is the site's generators,
 * and it also feeds the /404 page's "everything on the site" list. A policy is
 * not a peer of the worksheet pages, so the footer gives these their own row
 * instead (see SiteFooter, and the copy of it in scripts/prerender.js).
 */
export function contentRoutes(): RouteSeo[] {
    return Routes.filter((route) => route.contentPage === true && !route.noindex);
}

/**
 * Every page worth linking to from the site footer, in the order they should
 * appear. The footer is the only crawlable path between pages - the side nav
 * navigates by assigning to window.location, which search engines cannot follow.
 */
export function linkableRoutes(): RouteSeo[] {
    return [
        ...landingRoutes(),
        ...hubRoutes(),
        ...packRoutes(),
        ...chartRoutes(),
        ...flashCardRoutes(),
        ...quizRoutes(),
        ...dailyPuzzleRoutes(),
        ...cardGameRoutes(),
    ];
}

/**
 * The index page every other page in `locale` links to, and the hub of that
 * locale's link graph (ADR 0027, extended per-locale by ADR 0051 §8).
 * Defaults to English so every pre-existing call site keeps finding
 * `/all-worksheets` exactly as before. Undefined only if a locale has no
 * index of its own, which `prerender.test.js`'s per-locale crawl guarantee
 * asserts against directly.
 */
export function indexRoute(locale: Locale = DEFAULT_LOCALE): RouteSeo | undefined {
    return Routes.find((route) => route.indexPage === true && localeOf(route) === locale);
}

/** Every locale's index page, for registering one route per locale. */
export function indexRoutes(): RouteSeo[] {
    return Routes.filter((route) => route.indexPage === true);
}

/** Absolute canonical URL for a slug, e.g. https://math-pages.com/multiplication-worksheets */
export function canonicalUrl(slug: string): string {
    if (slug === "/") {
        return SITE_ORIGIN;
    }
    return SITE_ORIGIN + slug;
}
